import { FC, ReactNode, useId, useState } from "react";
import { msg, t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  ChevronDownSmallIcon,
  ChevronUpSmallIcon,
  CloseSmallIcon,
} from "@otrium/icons";
import { BaseMoney } from "src/types/ctgraphql.d";
import { CTPrice } from "src/atoms/CTPrice";
import { AppliedDiscount, groupDiscounts } from "src/utils/appliedDiscounts";
import styles from "./OrderSummaryDiscounts.module.scss";

type Props = {
  discounts: AppliedDiscount[];
  onRemove?: (code: string) => void;
  dataTestIdPrefix: string;
};

type RowProps = {
  label: ReactNode;
  amount: BaseMoney | null;
  amountTestId: string;
  remove?: { label: string; testId: string; onClick: () => void };
  testId?: string;
};

const Row: FC<RowProps> = ({ label, amount, amountTestId, remove, testId }) => (
  <div className={styles.row} data-testid={testId}>
    <span className={styles.label}>
      {label}
      {remove && (
        <button
          type="button"
          className={styles.remove}
          aria-label={remove.label}
          data-testid={remove.testId}
          onClick={remove.onClick}
        >
          <CloseSmallIcon aria-hidden="true" />
        </button>
      )}
    </span>
    {amount && (
      <span className={styles.amount} data-testid={amountTestId}>
        - <CTPrice price={amount} />
      </span>
    )}
  </div>
);

const OrderSummaryDiscounts: FC<Props> = ({
  discounts,
  onRemove,
  dataTestIdPrefix,
}) => {
  const { i18n } = useLingui();
  const listId = useId();
  const [isListOpen, setIsListOpen] = useState(false);

  const { promos, storeCredit, promoTotal } = groupDiscounts(discounts);
  const promoCount = promos.length;

  const promoRow = ({ code, amount }: AppliedDiscount) => (
    <Row
      key={code}
      label={
        <span>
          <Trans>Promo code</Trans>:{" "}
          <span
            className={styles.code}
            data-testid={`${dataTestIdPrefix}coupon-code`}
          >
            {code}
          </span>
        </span>
      }
      amount={amount}
      amountTestId="cart-line-items-discount"
      remove={
        onRemove && {
          label: t(i18n)`Remove promo code ${code}`,
          testId: "cart-delete-coupon-button",
          onClick: () => onRemove(code),
        }
      }
    />
  );

  return (
    <>
      {promoCount === 1 && promoRow(promos[0])}

      {promoCount > 1 && (
        <>
          <Row
            label={
              <button
                type="button"
                className={styles.toggle}
                aria-expanded={isListOpen}
                aria-controls={listId}
                data-testid={`${dataTestIdPrefix}promo-codes-toggle`}
                onClick={() => setIsListOpen((open) => !open)}
              >
                {i18n._(msg`promo_codes_count`.id, { count: promoCount })}
                {isListOpen ? (
                  <ChevronUpSmallIcon aria-hidden="true" />
                ) : (
                  <ChevronDownSmallIcon aria-hidden="true" />
                )}
              </button>
            }
            amount={promoTotal}
            amountTestId={`${dataTestIdPrefix}promo-codes-discount`}
          />
          <div id={listId} hidden={!isListOpen}>
            {promos.map(promoRow)}
          </div>
        </>
      )}

      {storeCredit && (
        <Row
          testId={`${dataTestIdPrefix}o-credit`}
          label={<Trans>O-credit</Trans>}
          amount={storeCredit.amount}
          amountTestId="cart-store-credit-discount"
          remove={
            onRemove && {
              label: t(i18n)`Remove O-credit`,
              testId: "cart-remove-o-credit-button",
              onClick: () => onRemove(storeCredit.code),
            }
          }
        />
      )}
    </>
  );
};

export default OrderSummaryDiscounts;
